import { Resources } from 'constants/resources';

import { orderAccept, orderDecline } from 'config/requests/orders';
import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify, useRecordContext } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import { BaseDialog } from 'shared/mui/Dialogs';
import { Typography } from 'shared/mui/Typography';
import { Order } from 'types/embedded/order';

import { StyledBox, StyledWarningAmberIcon } from './OrdersShowCommonInfoConfirmation.styled';

export const OrdersShowCommonInfoConfirmation = () => {
  const translate = useTypedTranslate();

  const record = useRecordContext<Order>();
  const { originId = '', gateway } = record ?? {};

  const notify = useNotify();

  const {
    openRecordModal: openAcceptDialog,
    handleOpenRecordModal: handleOpenAcceptDialog,
    handleCloseRecordModal: handleCloseAcceptDialog,
  } = useRecordModal();
  const {
    openRecordModal: openDeclineDialog,
    handleOpenRecordModal: handleOpenDeclineDialog,
    handleCloseRecordModal: handleCloseDeclineDialog,
  } = useRecordModal();
  const {
    openRecordModal: openRedirectAcceptDialog,
    handleOpenRecordModal: handleOpenRedirectAcceptDialog,
    handleCloseRecordModal: handleCloseRedirectAcceptDialog,
  } = useRecordModal();
  const {
    openRecordModal: openRedirectDeclineDialog,
    handleOpenRecordModal: handleOpenRedirectDeclineDialog,
    handleCloseRecordModal: handleCloseRedirectDeclineDialog,
  } = useRecordModal();

  const navigate = useNavigate();

  const handleOrderAccept = () => {
    handleCloseAcceptDialog();
    orderAccept(originId)
      .then(() => {
        notify(`${translate('app.orders.theOrderApprovedSuccessfully')}.`, { type: 'success' });
        handleOpenRedirectAcceptDialog();
      })
      .catch((response) => {
        const error =
          response.data.errors?.[0]?.title || `${translate('app.orders.failedToApproveTheOrder')}.`;
        notify(`${translate('app.common.error')}: ${error}`, { type: 'error' });
      });
  };

  const handleOrderDecline = () => {
    handleCloseDeclineDialog();
    orderDecline(originId)
      .then(() => {
        notify(`${translate('app.orders.theOrderHasBeenRejectedSuccessfully')}.`, {
          type: 'success',
        });
        handleOpenRedirectDeclineDialog();
      })
      .catch((response) => {
        const error =
          response.data.errors?.[0]?.title || `${translate('app.orders.failedToRejectTheOrder')}.`;
        notify(`${translate('app.common.error')}: ${error}`, { type: 'error' });
      });
  };

  const refetchQueries = () => {
    return Promise.all([
      queryClient.refetchQueries({ queryKey: [Resources.Orders] }),
      queryClient.refetchQueries({ queryKey: ['order_status_histories'] }),
      queryClient.refetchQueries({ queryKey: ['transactions'] }),
    ]);
  };

  return (
    <StyledBox>
      <StyledWarningAmberIcon />
      <Box style={{ marginLeft: '12px', marginRight: '16px', flex: 1 }}>
        <Typography sx={{ fontWeight: 500, marginBottom: '6px' }} variant="body1">
          {translate('app.orders.orderReview')}
        </Typography>
        <Typography variant="body2">
          {translate('app.orders.testModeWarning', {
            originId,
            gatewayName: gateway?.name,
          })}
        </Typography>
      </Box>
      <Box
        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '8px' }}
      >
        <Button
          color="success"
          label={translate('app.buttons.approve')}
          onClick={handleOpenAcceptDialog}
          size="small"
          variant="contained"
        />
        <Button
          color="error"
          label={translate('app.buttons.reject')}
          onClick={handleOpenDeclineDialog}
          size="small"
          variant="contained"
        />
      </Box>
      <BaseDialog
        leftActionsSlot={[
          <Button
            color="success"
            key="submit-btn"
            label={translate('app.buttons.approve')}
            onClick={handleOrderAccept}
            variant="contained"
          />,
          <Button
            key="cancel-btn"
            label={translate('app.buttons.cancel')}
            onClick={handleCloseAcceptDialog}
            variant="outlined"
          />,
        ]}
        message={translate('app.orders.testModeWarning2', {
          originId,
          gatewayName: gateway?.name,
        })}
        onClose={handleCloseAcceptDialog}
        open={openAcceptDialog}
        title={translate('app.orders.orderApproval')}
      />
      <BaseDialog
        leftActionsSlot={[
          <Button
            color="error"
            key="decline-btn"
            label={translate('app.buttons.reject')}
            onClick={handleOrderDecline}
            variant="contained"
          />,
          <Button
            key="cancel-btn"
            label={translate('app.buttons.cancel')}
            onClick={handleCloseDeclineDialog}
            variant="outlined"
          />,
        ]}
        message={translate('app.orders.testModeWarning3', {
          originId,
          gatewayName: gateway?.name || '',
        })}
        onClose={handleCloseDeclineDialog}
        open={openDeclineDialog}
        title={translate('app.orders.orderRejection')}
      />
      <BaseDialog
        leftActionsSlot={
          <Button
            label={translate('app.orders.proceedToTheGatewaySettings')}
            onClick={() => {
              navigate(`/gateways/${encodeURIComponent(gateway?.id ? gateway.id : '')}`);
            }}
            variant="outlined"
          />
        }
        message={translate('app.orders.testModeWarning4', {
          originId,
          gatewayName: gateway?.name || '',
        })}
        onClose={async () => {
          await refetchQueries();
          handleCloseRedirectAcceptDialog();
        }}
        open={openRedirectAcceptDialog}
        title={translate('app.orders.theOrderIsApproved')}
      />
      <BaseDialog
        leftActionsSlot={
          <Button
            label={translate('app.orders.proceedToTheGatewaySettings')}
            onClick={() => {
              navigate(`/gateways/${encodeURIComponent(gateway?.id ? gateway.id : '')}`);
            }}
            variant="outlined"
          />
        }
        message={translate('app.orders.testModeWarning4', {
          originId,
          gatewayName: gateway?.name || '',
        })}
        onClose={async () => {
          await refetchQueries();
          handleCloseRedirectDeclineDialog();
        }}
        open={openRedirectDeclineDialog}
        title={translate('app.orders.theOrderIsRejected')}
      />
    </StyledBox>
  );
};
